import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ActivatedRoute, ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree } from '@angular/router';

/** Services */
import { AuthService } from '../services/auth.service';
import { StorageService } from '../services/storage.service';
import { StorageKey } from '../models/common';
import { ContextService } from '../services/context.service';
import {
  isExternalPaymentDetailsInvite,
  isVendorCreateRoute,
  resolveInviteId,
} from '../utils/invite-id.util';

@Injectable()
export class AuthGuard implements CanActivate {

  constructor(private router: Router, private route: ActivatedRoute, private authSvc: AuthService, private storageSvc: StorageService, public contextSvc: ContextService) { }

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree | Observable<boolean | UrlTree> | Promise<boolean | UrlTree> {

    const inviteId = resolveInviteId(route, state.url);
    const isVendorPaymentDetailLink = isExternalPaymentDetailsInvite(
      state.url,
      route.queryParams,
      route.queryParamMap
    );
    const isVendorInviteLink =
      !!inviteId && isVendorCreateRoute(state.url) && !isVendorPaymentDetailLink;

    const isExternalAssetForm = state.url.includes('/asset-management/new-asset');
    
    const cameFromExternalForm = sessionStorage.getItem('fromExternalAssetForm') === 'true';
    
    if (isVendorInviteLink || isVendorPaymentDetailLink || isExternalAssetForm) {
      // Set flag when accessing external form
      if (isExternalAssetForm) {
        sessionStorage.setItem('fromExternalAssetForm', 'true');
      }
      return new Observable<boolean>(observer => {
        observer.next(true);
        observer.complete();
      });
    }

    if (cameFromExternalForm && !isExternalAssetForm) {
      sessionStorage.removeItem('fromExternalAssetForm');
      this.storageSvc.remove(StorageKey.jwt).subscribe();
      this.storageSvc.remove(StorageKey.refreshToken).subscribe();
      localStorage.removeItem('selectedRoleId');
      return new Observable<boolean | UrlTree>(observer => {
        observer.next(this.router.createUrlTree(['/login']));
        observer.complete();
      });
    }

    return new Observable<boolean | UrlTree>(observer => {

      this.authSvc.isLoggedIn().subscribe((isLoggedIn: boolean) => {

        if (isLoggedIn) {

          this.contextSvc.loadInitialSelectedRoleId();
          sessionStorage.removeItem('fromExternalAssetForm');
          observer.next(true);

        }
        else {

          // Return a UrlTree instead of calling router.navigateByUrl() here.
          // Imperatively navigating while this guard's own navigation is still
          // resolving races with the Router's internal transition and can leave
          // it stuck (no NavigationEnd), so further clicks silently do nothing
          // until a full page reload.
          observer.next(this.router.createUrlTree(['/login']));

        }

        observer.complete();

      });

    });

  }

}
